import type { PublicClient } from 'viem';
import { adapterAbi, dataProviderAbi, erc20Abi, faucetAbi, feedAbi, multicall3Abi, oracleAbi, poolAbi, rateStrategyAbi, stockTokenAbi } from './abis';
import { compareReserves } from './assets-meta';
import { MARKET } from './market.generated';
import type { Address, MarketSnapshot, ReserveSnapshot } from './types';

type Call = { address: Address; abi: readonly unknown[]; functionName: string; args?: readonly unknown[] };
type Result = { status: 'success'; result: unknown } | { status: 'failure'; error: Error };

const CALLS_PER_ASSET = 12;
/** StockFaucet of the market; the token address is used as a harmless placeholder target on deployments without one. */
const faucetOf = (a: { address: Address }): Address => MARKET.stockTokens?.faucet ?? a.address;

/**
 * Whole-market read in a single Multicall3 eth_call. Runs unchanged in the Worker (edge snapshot) and in the
 * browser (fallback when the API is unreachable). Oracle calls are allowed to fail: a dead feed must not blank the page.
 */
export async function readMarket(client: PublicClient, origin: MarketSnapshot['origin']): Promise<MarketSnapshot> {
  const dp = MARKET.protocolDataProvider;
  const calls: Call[] = [
    { address: MARKET.multicall3, abi: multicall3Abi, functionName: 'getBlockNumber' },
    { address: MARKET.multicall3, abi: multicall3Abi, functionName: 'getCurrentBlockTimestamp' },
  ];
  for (const a of MARKET.assets) {
    calls.push(
      { address: MARKET.aaveOracle, abi: oracleAbi, functionName: 'getAssetPrice', args: [a.address] },
      { address: dp, abi: dataProviderAbi, functionName: 'getReserveConfigurationData', args: [a.address] },
      { address: dp, abi: dataProviderAbi, functionName: 'getReserveData', args: [a.address] },
      { address: dp, abi: dataProviderAbi, functionName: 'getReserveCaps', args: [a.address] },
      { address: dp, abi: dataProviderAbi, functionName: 'getPaused', args: [a.address] },
      { address: dp, abi: dataProviderAbi, functionName: 'getReserveTokensAddresses', args: [a.address] },
      { address: MARKET.pool, abi: poolAbi, functionName: 'getVirtualUnderlyingBalance', args: [a.address] },
      { address: a.adapter, abi: adapterAbi, functionName: 'status' },
      { address: a.feed, abi: feedAbi, functionName: 'owner' },
      { address: MARKET.interestRateStrategy, abi: rateStrategyAbi, functionName: 'getInterestRateDataBps', args: [a.address] },
      // stock token extras (both calls simply fail on the plain USDG mock and on older deployments)
      { address: a.address, abi: stockTokenAbi, functionName: 'paused' },
      { address: faucetOf(a), abi: faucetAbi, functionName: 'drips', args: [a.address] },
    );
  }
  const res = (await client.multicall({ contracts: calls as never, allowFailure: true })) as Result[];
  const ok = <T>(r: Result | undefined): T | null => (r && r.status === 'success' ? (r.result as T) : null);
  const must = <T>(r: Result | undefined, what: string): T => {
    const v = ok<T>(r);
    if (v === null) throw new Error(`market read failed: ${what}`);
    return v;
  };

  const reserves: ReserveSnapshot[] = MARKET.assets.map((a, i) => {
    const r = res.slice(2 + i * CALLS_PER_ASSET, 2 + (i + 1) * CALLS_PER_ASSET);
    const price = ok<bigint>(r[0]);
    const cfg = must<readonly [bigint, bigint, bigint, bigint, bigint, boolean, boolean, boolean, boolean, boolean]>(r[1], `${a.symbol} configuration`);
    const data = must<readonly bigint[]>(r[2], `${a.symbol} reserve data`);
    const caps = must<readonly [bigint, bigint]>(r[3], `${a.symbol} caps`);
    const tokens = must<readonly [Address, Address, Address]>(r[5], `${a.symbol} tokens`);
    const status = ok<readonly [bigint, bigint, bigint, boolean, bigint, bigint, bigint]>(r[7]);
    const rate = ok<{ optimalUsageRatio: number; baseVariableBorrowRate: number; variableRateSlope1: number; variableRateSlope2: number }>(r[9]);
    const drip = ok<readonly [bigint, number, boolean]>(r[11]);
    const priceFailure = r[0]?.status === 'failure' ? r[0].error : null;
    return {
      symbol: a.symbol,
      name: a.name,
      address: a.address,
      decimals: a.decimals,
      role: a.role,
      priceUsd: (price ?? 0n).toString(),
      priceError: priceFailure ? (/FeedDead/.test(priceFailure.message) ? 'FeedDead' : 'OracleError') : null,
      ltvBps: Number(cfg[1]),
      liquidationThresholdBps: Number(cfg[2]),
      liquidationBonusBps: cfg[3] > 10_000n ? Number(cfg[3] - 10_000n) : 0,
      reserveFactorBps: Number(cfg[4]),
      usageAsCollateralEnabled: cfg[5],
      borrowingEnabled: cfg[6],
      isActive: cfg[8],
      isFrozen: cfg[9],
      isPaused: ok<boolean>(r[4]) ?? false,
      borrowCap: caps[0].toString(),
      supplyCap: caps[1].toString(),
      totalSupplied: data[2]!.toString(),
      totalDebt: (data[3]! + data[4]!).toString(),
      availableLiquidity: (ok<bigint>(r[6]) ?? 0n).toString(),
      supplyRateRay: data[5]!.toString(),
      borrowRateRay: data[6]!.toString(),
      aToken: tokens[0],
      variableDebtToken: tokens[2],
      oracle: {
        updatedAt: status ? Number(status[1]) : 0,
        oraclePaused: status ? status[3] : false,
        uiMultiplier: status ? status[4].toString() : '0',
        pendingMultiplier: status ? status[5].toString() : '0',
        pendingEffectiveAt: status ? Number(status[6]) : 0,
        feedOwner: ok<Address>(r[8]),
      },
      tokenPaused: ok<boolean>(r[10]) ?? false,
      faucet:
        a.faucet === 'stock'
          ? { kind: 'stock', amount: drip ? drip[0].toString() : '0', cooldown: drip ? Number(drip[1]) : 0, enabled: drip ? drip[2] : false }
          : { kind: 'mint', amount: (10_000n * 10n ** BigInt(a.decimals)).toString(), cooldown: 0, enabled: true },
      rateModel:
        rate
          ? { optimalUsageBps: rate.optimalUsageRatio, baseRateBps: rate.baseVariableBorrowRate, slope1Bps: rate.variableRateSlope1, slope2Bps: rate.variableRateSlope2 }
          : null,
    };
  });

  return {
    chainId: MARKET.chainId,
    block: Number(must<bigint>(res[0], 'block number')),
    blockTimestamp: Number(must<bigint>(res[1], 'block timestamp')),
    fetchedAt: Date.now(),
    origin,
    // USDG first, then the stocks by market cap: every list in the app follows the snapshot's order
    reserves: reserves.sort(compareReserves),
  };
}

export interface UserReserve {
  symbol: string;
  walletBalance: bigint;
  supplied: bigint;
  debt: bigint;
  allowance: bigint;
  usedAsCollateral: boolean;
  /** Stock faucet: unix time from which this account may claim again (0 = now); null when not claimable through the faucet. */
  faucetNextClaimAt: number | null;
}
export interface UserSnapshot {
  address: Address;
  nativeBalance: bigint;
  totalCollateralBase: bigint;
  totalDebtBase: bigint;
  availableBorrowsBase: bigint;
  currentLiquidationThresholdBps: number;
  ltvBps: number;
  healthFactor: bigint;
  /** False when getUserAccountData reverted (dead feed): balances below are still valid. */
  accountOk: boolean;
  reserves: Record<string, UserReserve>;
}

const USER_CALLS_PER_ASSET = 4;

export async function readUser(client: PublicClient, user: Address): Promise<UserSnapshot> {
  const calls: Call[] = [
    { address: MARKET.multicall3, abi: multicall3Abi, functionName: 'getEthBalance', args: [user] },
    { address: MARKET.pool, abi: poolAbi, functionName: 'getUserAccountData', args: [user] },
  ];
  for (const a of MARKET.assets) {
    calls.push(
      { address: a.address, abi: erc20Abi, functionName: 'balanceOf', args: [user] },
      { address: a.address, abi: erc20Abi, functionName: 'allowance', args: [user, MARKET.pool] },
      { address: MARKET.protocolDataProvider, abi: dataProviderAbi, functionName: 'getUserReserveData', args: [a.address, user] },
      { address: faucetOf(a), abi: faucetAbi, functionName: 'nextClaimAt', args: [a.address, user] },
    );
  }
  const res = (await client.multicall({ contracts: calls as never, allowFailure: true })) as Result[];
  const ok = <T>(r: Result | undefined): T | null => (r && r.status === 'success' ? (r.result as T) : null);
  const acct = ok<readonly [bigint, bigint, bigint, bigint, bigint, bigint]>(res[1]);
  const reserves: Record<string, UserReserve> = {};
  MARKET.assets.forEach((a, i) => {
    const r = res.slice(2 + i * USER_CALLS_PER_ASSET, 2 + (i + 1) * USER_CALLS_PER_ASSET);
    const urd = ok<readonly [bigint, bigint, bigint, bigint, bigint, bigint, bigint, number, boolean]>(r[2]);
    const next = a.faucet === 'stock' ? ok<bigint>(r[3]) : null;
    reserves[a.symbol] = {
      symbol: a.symbol,
      walletBalance: ok<bigint>(r[0]) ?? 0n,
      allowance: ok<bigint>(r[1]) ?? 0n,
      supplied: urd ? urd[0] : 0n,
      debt: urd ? urd[1] + urd[2] : 0n,
      usedAsCollateral: urd ? urd[8] : false,
      // type(uint256).max means "not enabled in the faucet"
      faucetNextClaimAt: next === null || next > 1n << 62n ? null : Number(next),
    };
  });
  return {
    address: user,
    nativeBalance: ok<bigint>(res[0]) ?? 0n,
    totalCollateralBase: acct ? acct[0] : 0n,
    totalDebtBase: acct ? acct[1] : 0n,
    availableBorrowsBase: acct ? acct[2] : 0n,
    currentLiquidationThresholdBps: acct ? Number(acct[3]) : 0,
    ltvBps: acct ? Number(acct[4]) : 0,
    healthFactor: acct ? acct[5] : 0n,
    accountOk: acct !== null,
    reserves,
  };
}
